import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import Modal from "../../Elements/Modal/Modal";
import MainButton from "../../Elements/MainButton/MainButton";
import SecondButton from "../../Elements/SecondButton/SecondButton";
import { fetchPackageById } from "../../../services/PackagesService";
import trashIcon from "../../../assets/icons/common/trash.png";
import "./AddedToCartModal.css";

const AddedToCartModal = ({ isOpen, onClose, onGoToCart, onRemoveItem, items = [] }) => {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.resolvedLanguage || i18n.language || "en";

  const [translatedNames, setTranslatedNames] = useState({});
  const [loadingNames, setLoadingNames] = useState(false);

  const currency = (value, cur = "USD") => {
    if (value == null) return "";
    return `${value.toFixed(2)} ${cur}`;
  };

  const sortedItems = [...items].sort((a, b) => (b.lastAddedAt || 0) - (a.lastAddedAt || 0));
  const grandTotal = sortedItems.reduce((sum, item) => sum + (item.price || 0) * (item.quantity || 1), 0);

  useEffect(() => {
    if (isOpen && items.length === 0) {
      onClose();
    }
  }, [isOpen, items.length, onClose]);

  useEffect(() => {
    if (!isOpen || items.length === 0) return;

    let isCancelled = false;

    const loadTranslations = async () => {
      setLoadingNames(true);
      try {
        const uniqueItems = Array.from(new Set(items.map((it) => it.internal_package_id || it.id)));

        const results = await Promise.all(
          uniqueItems.map(async (pid) => {
            try {
              const targetCurrency = items.find((it) => (it.internal_package_id || it.id) === pid)?.currency || "EUR";
              const data = await fetchPackageById(pid, targetCurrency, currentLang);
              return { id: pid, name: data.name };
            } catch (err) {
              console.error(`Failed to fetch package translation for ${pid}`, err);
              return null;
            }
          }),
        );

        if (!isCancelled) {
          const namesMap = {};
          results.filter(Boolean).forEach((res) => {
            namesMap[res.id] = res.name;
          });
          setTranslatedNames(namesMap);
        }
      } finally {
        if (!isCancelled) {
          setLoadingNames(false);
        }
      }
    };

    loadTranslations();

    return () => {
      isCancelled = true;
    };
  }, [isOpen, items, currentLang]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t("offers.addedToCart") || "Added to cart"}
      actions={
        <>
          <SecondButton onClick={onClose}>{t("offers.continueShopping") || "Continue shopping"}</SecondButton>
          <MainButton onClick={onGoToCart}>{t("offers.viewCart") || "Go to cart"}</MainButton>
        </>
      }
    >
      <div className="added-to-cart-body">
        <p className="added-to-cart-text">
          {t("offers.addedToCartText") || "Your eSIM plan has been added to your cart."}
        </p>

        <div className="added-to-cart-list">
          {sortedItems.map((item) => {
            const itemId = item.internal_package_id || item.id;
            const displayName = translatedNames[itemId] || item.name;

            return (
              <div key={item.id} className="added-to-cart-item">
                <div className="added-to-cart-item__name">{displayName}</div>

                <div className="added-to-cart-item__right">
                  <div className="added-to-cart-item__calc">
                    {item.quantity || 1} × {currency(item.price, item.currency)}
                  </div>

                  <button
                    type="button"
                    className="added-to-cart-item__remove"
                    onClick={() => onRemoveItem(item.id)}
                    aria-label="Remove from cart"
                  >
                    <img src={trashIcon} alt="" className="added-to-cart-item__remove-icon" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        <div className="added-to-cart-summary">
          <span className="added-to-cart-summary__label">{t("cart.total") || "Total"}</span>
          <span className="added-to-cart-summary__value">
            {currency(grandTotal, sortedItems[0]?.currency || "USD")}
          </span>
        </div>
      </div>
    </Modal>
  );
};

AddedToCartModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onGoToCart: PropTypes.func.isRequired,
  onRemoveItem: PropTypes.func.isRequired,
  items: PropTypes.array,
};

export default AddedToCartModal;
